import { ChangeEvent, useEffect, useRef, useState, FocusEvent } from 'react';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import { Input, ToggleBox } from '@unified-checkout/design-system';

import type { IFields } from '@app/dashboard/types/innerLayout/payment_links/single_quick_link';
import { validateField } from '@app/dashboard/utils/formValidations';
import {
  MAX_SHARE_WITH_ATTEMPTS,
  PAYMENT_NUMBER_INITIAL_VALUE,
  SINGLE_PAYMENTS_FIELDS,
} from '@app/dashboard/constants/payment-links/single-payments';
import {
  SINGLE_PAYMENT_LINKS_EVENTS,
  SINGLE_PAYMENT_LINKS_EVENTS_PARAMS,
} from '@app/dashboard/constants/tracking_events/payment_links/single';

import styles from './unlimited-payments.module.scss';

const UnlimitedPayments = ({ fields, setFields }: IFields) => {
  const { t } = useTranslation();
  const [shouldHaveUnlimitedPayments, setShouldHaveUnlimitedPayments] =
    useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const maxAttemptsRef = useRef(0);

  const handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const numericValue = Number(inputValue);
    if (numericValue < 0) return;

    const exceedsMaxLimit = numericValue >= MAX_SHARE_WITH_ATTEMPTS;

    const validationErrorMessage = exceedsMaxLimit
      ? t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.restrictions.errors.maximumPaymentNumber',
          { max_number: MAX_SHARE_WITH_ATTEMPTS }
        )
      : '';

    const isFieldValid = !validationErrorMessage;

    setFields((previousFields) => ({
      ...previousFields,
      [SINGLE_PAYMENTS_FIELDS.PAYMENT_LIMIT]: {
        ...previousFields[SINGLE_PAYMENTS_FIELDS.PAYMENT_LIMIT],
        value: inputValue,
        isValid: isFieldValid,
        errorMsg: validationErrorMessage,
      },
    }));

    if (isFieldValid) {
      validateField(
        fields,
        setFields,
        SINGLE_PAYMENTS_FIELDS.PAYMENT_LIMIT,
        inputValue
      );
    }
  };

  const handleToggleChange = (checked: boolean) => {
    if (!isTouched) return;
    setShouldHaveUnlimitedPayments(checked);

    amplitude.track(SINGLE_PAYMENT_LINKS_EVENTS.RESTRICTIONS_UPDATED, {
      [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.UNLIMITED_PAYMENTS]: checked,
    });
  };

  const onFocusHandler = (e: FocusEvent<HTMLInputElement>) => {
    maxAttemptsRef.current = e.target.value ? Number(e.target.value) : 0;
  };

  const onBlurHandler = (e: FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value && value !== maxAttemptsRef.current.toString()) {
      amplitude.track(SINGLE_PAYMENT_LINKS_EVENTS.RESTRICTIONS_UPDATED, {
        [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.MAX_PAYMENTS]: value,
      });
    }
  };

  useEffect(() => {
    if (!shouldHaveUnlimitedPayments) {
      setFields((prev: any) => {
        return {
          ...prev,
          ...PAYMENT_NUMBER_INITIAL_VALUE,
        };
      });
    } else {
      setFields((prev: any) => {
        const updatedFields = { ...prev };
        delete updatedFields[SINGLE_PAYMENTS_FIELDS.PAYMENT_LIMIT];
        return updatedFields;
      });
    }
  }, [shouldHaveUnlimitedPayments]);

  return (
    <div className={styles['unlimited-payments']}>
      <ToggleBox
        size="sm"
        type="transparent box"
        text={t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.restrictions.unlimitedPayments'
        )}
        hintText={t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.restrictions.unlimitedPaymentsDescription'
        )}
        checked={shouldHaveUnlimitedPayments}
        onToggleChangeHandler={handleToggleChange}
        onBeforeToggleConfirmation={async () => {
          if (!isTouched) {
            setIsTouched(true);
          }
          return true;
        }}
      />
      {!shouldHaveUnlimitedPayments && (
        <Input
          name={SINGLE_PAYMENTS_FIELDS.PAYMENT_LIMIT}
          type="number"
          value={fields[SINGLE_PAYMENTS_FIELDS.PAYMENT_LIMIT]?.value}
          placeholder={t(
            'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.restrictions.placeholders.numberOfPaymentsPlaceholder'
          )}
          onChange={handleFieldChange}
          error={fields[SINGLE_PAYMENTS_FIELDS.PAYMENT_LIMIT]?.errorMsg}
          status={
            fields[SINGLE_PAYMENTS_FIELDS.PAYMENT_LIMIT]?.errorMsg
              ? 'error'
              : 'default'
          }
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
        />
      )}
    </div>
  );
};

export default UnlimitedPayments;
